import { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Check,
  X,
  Plus,
  Save,
  Bell,
  Clock,
  Users,
  Star,
  Mail,
  Shield,
  AlertTriangle,
  Hash,
  Globe,
  UserPlus,
  ChevronDown,
  Zap,
} from 'react-feather';
import AddPortfolioRulesModal, { ALL_RULES } from './AddPortfolioRulesModal';
import type { AddedRule, Rule, Param } from './AddPortfolioRulesModal';
import {
  CATEGORIES,
  COLOR_MAP,
  CAT_ORDER,
  FREQUENCY_OPTIONS,
  FREQUENCY_COLORS,
  LANGUAGE_OPTIONS,
  getRuleCategory,
  getRuleName,
} from './portfolioTypes';
import type {
  CreditSafePortfolio,
  CategoryKey,
  ColorType,
  CreditsafePortfolioRule,
  FrequencyOption,
  EmailRecipient,
  PortfolioEditState,
} from './portfolioTypes';
import SelectCreditSafeCompanyModal from './SelectCreditSafeCompanyModal';
import { useAppSelector } from '../../../../redux/store';
import { BuildingOffice } from '@phosphor-icons/react';
import { DillButton, DillInput, DillSelect, DillToggle } from '../../../general/Dill';

// ─────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────

export type PortfolioDetailProps = {
  portfolio: CreditSafePortfolio;
  /** Single source of truth — both API-sourced and custom rules live here */
  rules: CreditsafePortfolioRule[];
  onBack: () => void;
  onSavePortfolio: (updated: PortfolioEditState) => void;
  onRulesChange: (rules: CreditsafePortfolioRule[]) => void;
  onEditCompany: (company: { creditSafeId: string; name: string }[]) => void;
};

// ─────────────────────────────────────────────
// CONVERSION HELPERS
// ─────────────────────────────────────────────

/** Convert an AddedRule (modal output) to the wire format */
function addedRuleToApiRule(ar: AddedRule): CreditsafePortfolioRule {
  const toStr = (v: string | number | boolean | undefined) =>
    v !== undefined && v !== '' && v !== false ? String(v) : undefined;
  return {
    isActive: 1,
    ruleCode: ar.rule.id,
    ruleCountryCode: ar.rule.id >= 1600 && ar.rule.id < 1700 ? 'US' : 'XX',
    ruleType: 1,
    ruleTypeName: ar.rule.name,
    name: ar.rule.name,
    param0: toStr(ar.paramValues.param0),
    param1: toStr(ar.paramValues.param1),
    param2: toStr(ar.paramValues.param2),
  };
}

/** Convert a CreditsafePortfolioRule back to AddedRule so the modal can pre-populate it */
function apiRuleToAddedRule(r: CreditsafePortfolioRule): AddedRule | null {
  const rule = ALL_RULES.find((ar) => ar.id === r.ruleCode);
  if (!rule) return null;
  const paramValues: Record<string, string | number | boolean> = {};
  if (r.param0 !== undefined) paramValues.param0 = r.param0;
  if (r.param1 !== undefined) paramValues.param1 = r.param1;
  if (r.param2 !== undefined) paramValues.param2 = r.param2;
  return { rule, paramValues, isActive: r.isActive === 1 };
}

// ─────────────────────────────────────────────
// SHARED ATOMS
// ─────────────────────────────────────────────

function CategoryBadge({ category }: { category: CategoryKey }) {
  const cat = CATEGORIES[category];
  const colors = COLOR_MAP[cat.color as ColorType];
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${colors.badge}`}>
      {cat.icon} {cat.label}
    </span>
  );
}

function ConfirmDeleteStrip({
  name,
  onConfirm,
  onCancel,
}: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3">
      <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-red-700">Remove "{name}"?</p>
        <p className="text-[11px] text-red-500">This rule will stop monitoring this condition.</p>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg px-2.5 py-1.5">
          Remove
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// RICH PARAM EDITOR (for rules in ALL_RULES)
// ─────────────────────────────────────────────

function RichParamEditor({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-700">{param.userLabel}</label>
      <p className="text-[11px] text-gray-400 leading-snug">{param.description}</p>
      <div className="mt-1">
        {param.type === 'select' && (
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white">
            {(param.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o === '' ? 'No preference' : o}
              </option>
            ))}
          </select>
        )}
        {param.type === 'toggle' && (
          <div className="flex items-center gap-2">
            <DillToggle value={typeof value === 'boolean' ? value : false} onChange={onChange} />
            <span className="text-xs text-gray-500">{value ? 'Yes' : 'No'}</span>
          </div>
        )}
        {param.type === 'number' && (
          <input
            type="number"
            value={value === '' ? '' : typeof value === 'number' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={param.placeholder}
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white placeholder-gray-300"
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// RAW PARAM EDITOR (for rules not in ALL_RULES)
// ─────────────────────────────────────────────

function RawParamEditor({
  rule,
  localParams,
  onChange,
}: {
  rule: CreditsafePortfolioRule;
  localParams: { p0: string; p1: string; p2: string };
  onChange: (p: { p0: string; p1: string; p2: string }) => void;
}) {
  // Infer human labels from ruleTypeName
  const labels = useMemo(() => {
    const tn = (rule.ruleTypeName ?? '').toLowerCase();
    if (tn.includes('band') || tn.includes('rating')) {
      return [
        'Bands to drop (e.g. 1, 2)',
        'Threshold band (A–E or Any)',
        'Return band in response (true/false)',
      ];
    }
    if (tn.includes('limit'))
      return ['Any change (true/false)', 'Decrease % threshold', 'Absolute floor value'];
    if (tn.includes('dbt') || tn.includes('days'))
      return ['Minimum % change', 'Minimum delta (days)', ''];
    return ['Parameter 0', 'Parameter 1', 'Parameter 2'];
  }, [rule.ruleTypeName]);

  return (
    <div className="space-y-3">
      {[
        {
          label: labels[0],
          val: localParams.p0,
          set: (v: string) => onChange({ ...localParams, p0: v }),
        },
        {
          label: labels[1],
          val: localParams.p1,
          set: (v: string) => onChange({ ...localParams, p1: v }),
        },
        ...(labels[2]
          ? [
              {
                label: labels[2],
                val: localParams.p2,
                set: (v: string) => onChange({ ...localParams, p2: v }),
              },
            ]
          : []),
      ].map((f, i) => (
        <div key={i}>
          <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
          <input
            value={f.val}
            onChange={(e) => f.set(e.target.value)}
            placeholder="Leave empty to clear"
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white font-mono placeholder-gray-300"
          />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// UNIFIED INLINE EDIT PANEL
// ─────────────────────────────────────────────

function InlineEditPanel({
  rule,
  knownRule,
  onSave,
  onCancel,
}: {
  rule: CreditsafePortfolioRule;
  knownRule: Rule | null;
  onSave: (updated: Partial<CreditsafePortfolioRule>) => void;
  onCancel: () => void;
}) {
  // Rich mode: for rules we know (in ALL_RULES)
  const [richValues, setRichValues] = useState<Record<string, string | number | boolean>>(() => {
    const v: Record<string, string | number | boolean> = {};
    if (rule.param0 !== undefined) v.param0 = rule.param0;
    if (rule.param1 !== undefined) v.param1 = rule.param1;
    if (rule.param2 !== undefined) v.param2 = rule.param2;
    return v;
  });

  // Raw mode: for unknown rules
  const [rawParams, setRawParams] = useState({
    p0: rule.param0 ?? '',
    p1: rule.param1 ?? '',
    p2: rule.param2 ?? '',
  });

  const handleSave = () => {
    if (knownRule) {
      const toStr = (v: string | number | boolean | undefined) =>
        v !== undefined && v !== '' && v !== false ? String(v) : undefined;
      onSave({
        param0: toStr(richValues.param0),
        param1: toStr(richValues.param1),
        param2: toStr(richValues.param2),
      });
    } else {
      onSave({
        param0: rawParams.p0 || undefined,
        param1: rawParams.p1 || undefined,
        param2: rawParams.p2 || undefined,
      });
    }
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-4">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Edit conditions
      </p>
      <div className="space-y-4 mb-4">
        {knownRule ? (
          knownRule.params.map((p) => (
            <RichParamEditor
              key={p.key}
              param={p}
              value={richValues[p.key] ?? ''}
              onChange={(v) => setRichValues((prev) => ({ ...prev, [p.key]: v }))}
            />
          ))
        ) : (
          <RawParamEditor rule={rule} localParams={rawParams} onChange={setRawParams} />
        )}
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <DillButton onClick={onCancel} label="Cancel" icon={<X size={12} />} variant="secondary" />
        <DillButton onClick={handleSave} label="Save" icon={<Check size={12} />} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PARAM CHIPS — shown on collapsed rows
// ─────────────────────────────────────────────

function ParamChips({
  rule,
  knownRule,
}: {
  rule: CreditsafePortfolioRule;
  knownRule: Rule | null;
}) {
  // Narrow param keys once
  const paramKeys: Array<'param0' | 'param1' | 'param2'> = ['param0', 'param1', 'param2'];

  const params = paramKeys.map((k) => rule[k]);

  const isNonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

  const nonEmpty = params.filter(isNonEmpty);

  if (nonEmpty.length === 0) return null;

  // ─────────────────────────────────────────────
  // Known rule (rich labels)
  // ─────────────────────────────────────────────
  if (knownRule) {
    const chips = knownRule.params
      .filter((p) => {
        const key = p.key as 'param0' | 'param1' | 'param2';
        const v = rule[key];
        return isNonEmpty(v);
      })
      .map((p) => {
        const key = p.key as 'param0' | 'param1' | 'param2';
        return {
          label: p.userLabel.replace(/^(alert me if|alert me when|notify me)/i, '').trim(),
          value: rule[key] as string,
        };
      });

    if (chips.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {chips.map((chip, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[11px] bg-gray-50 border border-gray-100 rounded-md px-2 py-1 text-gray-500">
            <span className="text-gray-400 truncate max-w-[130px]">{chip.label}</span>
            <span className="mx-1 text-gray-300">·</span>
            <span className="font-mono font-semibold text-indigo-600">{chip.value}</span>
          </span>
        ))}
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // Raw param chips (API-only rules)
  // ─────────────────────────────────────────────
  return (
    <div className="flex flex-wrap gap-1.5 mt-2.5">
      {nonEmpty.map((p, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-[11px] bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1 text-indigo-600">
          <span className="text-indigo-300 text-[10px]">{paramKeys[i]}</span>
          <span className="mx-0.5 text-indigo-200">·</span>
          <span className="font-mono font-semibold">{p}</span>
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// UNIFIED RULE ROW
// ─────────────────────────────────────────────

function RuleRow({
  rule,
  onToggle,
  onUpdate,
  onRemove,
}: {
  rule: CreditsafePortfolioRule;
  onToggle: () => void;
  onUpdate: (updated: Partial<CreditsafePortfolioRule>) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const knownRule = useMemo(
    () => ALL_RULES.find((r) => r.id === rule.ruleCode) ?? null,
    [rule.ruleCode]
  );
  const catKey = getRuleCategory(rule.ruleCode);
  const cat = CATEGORIES[catKey];
  const colors = COLOR_MAP[cat.color as ColorType];
  const isActive = rule.isActive === 1;
  const displayName = getRuleName(rule.ruleCode, rule.name);
  // Only show the Creditsafe source badge if the rule is NOT in our ALL_RULES definition
  const isApiOnly = knownRule === null;
  const hasParams =
    (rule.param0 !== undefined && rule.param0 !== '') ||
    (rule.param1 !== undefined && rule.param1 !== '') ||
    (rule.param2 !== undefined && rule.param2 !== '') ||
    (knownRule?.params?.length ?? 0) > 0;

  if (confirmRemove) {
    return (
      <ConfirmDeleteStrip
        name={displayName}
        onConfirm={onRemove}
        onCancel={() => setConfirmRemove(false)}
      />
    );
  }

  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden transition-all ${
        !isActive ? 'opacity-60' : ''
      } ${editing ? 'border-blue-200 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>
      <div className="flex items-start overflow-hidden">
        {/* Category colour strip — dimmed when off */}
        <div
          className={`w-1 self-stretch flex-shrink-0 transition-colors ${
            isActive ? colors.strip : 'bg-gray-200'
          }`}
        />

        <div className="flex-1 min-w-0 px-4 py-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                <span
                  className={`text-sm font-semibold ${
                    isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                  {displayName}
                </span>
                {knownRule?.subtitle && (
                  <span className="text-gray-400 text-xs hidden sm:block">
                    {knownRule.subtitle}
                  </span>
                )}
                {isApiOnly && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-indigo-500 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                    <Zap size={8} /> Creditsafe
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <CategoryBadge category={catKey} />
                {isApiOnly && (
                  <span className="text-[10px] text-gray-400 font-mono">#{rule.ruleCode}</span>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
              {/* Active toggle */}
              <div className="flex items-center gap-1.5">
                <DillToggle value={isActive} onChange={onToggle} />
                <span
                  className={`text-[10px] font-semibold w-7 ${
                    isActive ? 'text-emerald-600' : 'text-gray-400'
                  }`}>
                  {isActive ? 'On' : 'Off'}
                </span>
              </div>

              {/* Edit */}
              {hasParams && (
                <button
                  type="button"
                  onClick={() => setEditing((e) => !e)}
                  className={`inline-flex items-center gap-1 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-colors ${
                    editing
                      ? 'text-blue-700 bg-blue-50 border border-blue-200'
                      : 'text-gray-500 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                  <Edit2 size={11} />
                  {editing ? 'Editing' : 'Edit'}
                </button>
              )}

              {/* Delete */}
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Param chips */}
          {!editing && <ParamChips rule={rule} knownRule={knownRule} />}

          {/* Description for known paramless rules */}
          {!hasParams && !editing && knownRule?.userDescription && (
            <p className="text-[11px] text-gray-400 mt-1.5">{knownRule.userDescription}</p>
          )}
        </div>
      </div>

      {/* Inline edit panel */}
      {editing && (
        <InlineEditPanel
          rule={rule}
          knownRule={knownRule}
          onSave={(updated) => {
            onUpdate(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// CATEGORY SECTION
// ─────────────────────────────────────────────

function CategorySection({
  catKey,
  rules,
  onToggle,
  onUpdate,
  onRemove,
}: {
  catKey: CategoryKey;
  rules: CreditsafePortfolioRule[];
  onToggle: (ruleCode: number) => void;
  onUpdate: (ruleCode: number, updated: Partial<CreditsafePortfolioRule>) => void;
  onRemove: (ruleCode: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const cat = CATEGORIES[catKey];
  const colors = COLOR_MAP[cat.color as ColorType];

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center gap-2 w-full text-left mb-2.5 group py-1">
        <span className="text-base">{cat.icon}</span>
        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
          {cat.label}
        </span>
        <span
          className={`text-[10px] font-semibold border rounded-full px-1.5 py-0 ${colors.badge}`}>
          {rules.length}
        </span>
        <div className="flex-1 h-px bg-gray-100" />
        <ChevronDown
          size={13}
          className={`text-gray-300 transition-transform group-hover:text-gray-400 ${
            collapsed ? '-rotate-90' : ''
          }`}
        />
      </button>
      {!collapsed && (
        <div className="space-y-2">
          {rules.map((r, idx) => (
            <RuleRow
              key={`${r.ruleCode}-${idx}`}
              rule={r}
              onToggle={() => onToggle(r.ruleCode)}
              onUpdate={(updated) => onUpdate(r.ruleCode, updated)}
              onRemove={() => onRemove(r.ruleCode)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// UNIFIED RULES PANEL
// ─────────────────────────────────────────────

function RulesPanel({
  portfolio,
  rules,
  onRulesChange,
}: {
  portfolio: CreditSafePortfolio;
  rules: CreditsafePortfolioRule[];
  onRulesChange: (rules: CreditsafePortfolioRule[]) => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  // Handlers
  const handleToggle = (ruleCode: number) =>
    onRulesChange(
      rules.map((r) =>
        r.ruleCode === ruleCode ? { ...r, isActive: (r.isActive === 1 ? 0 : 1) as 0 | 1 } : r
      )
    );

  const handleUpdate = (ruleCode: number, updated: Partial<CreditsafePortfolioRule>) =>
    onRulesChange(rules.map((r) => (r.ruleCode === ruleCode ? { ...r, ...updated } : r)));

  const handleRemove = (ruleCode: number) =>
    onRulesChange(rules.filter((r) => r.ruleCode !== ruleCode));

  // Modal: convert CreditsafePortfolioRule[] → AddedRule[] for pre-population
  const modalExistingRules = useMemo(
    () =>
      rules.flatMap((r) => {
        const ar = apiRuleToAddedRule(r);
        return ar ? [ar] : [];
      }),
    [rules]
  );

  // Modal save: merge saved AddedRules back into the full rules list
  const handleModalSave = (saved: AddedRule[]) => {
    const savedCodes = new Set(saved.map((ar) => ar.rule.id));
    // Keep all rules that are NOT in ALL_RULES (pure API rules not managed by modal)
    const apiOnlyRules = rules.filter((r) => ALL_RULES.every((ar) => ar.id !== r.ruleCode));
    // Convert modal output to CreditsafePortfolioRule
    const modalRules = saved.map(addedRuleToApiRule);
    onRulesChange([...apiOnlyRules, ...modalRules]);
  };

  // Group by category
  const grouped = useMemo(() => {
    const g: Partial<Record<CategoryKey, CreditsafePortfolioRule[]>> = {};
    for (const r of rules) {
      const cat = getRuleCategory(r.ruleCode);
      if (!g[cat]) g[cat] = [];
      g[cat]!.push(r);
    }
    return g;
  }, [rules]);

  const activeCategories = CAT_ORDER.filter((c) => (grouped[c]?.length ?? 0) > 0);
  const activeCount = rules.filter((r) => r.isActive === 1).length;

  // Warn about known param rules with no params set
  const unconfigured = rules.filter((r) => {
    const knownRule = ALL_RULES.find((ar) => ar.id === r.ruleCode);
    if (!knownRule || knownRule.type !== 'param') return false;
    return !r.param0 && !r.param1 && !r.param2;
  });

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Bell size={15} className="text-blue-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Monitoring Rules</h3>
              <p className="text-xs text-gray-400">
                {rules.length === 0
                  ? 'No rules configured'
                  : `${activeCount} active · ${rules.length} total · ${
                      activeCategories.length
                    } categor${activeCategories.length === 1 ? 'y' : 'ies'}`}
              </p>
            </div>
          </div>
          <DillButton
            onClick={() => setModalOpen(true)}
            label="Add Rules"
            icon={<Plus size={13} />}
          />
        </div>

        {/* Legend */}
        {rules.some((r) => ALL_RULES.every((ar) => ar.id !== r.ruleCode)) && (
          <div className="px-5 pt-3 flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <span className="w-2 h-2 rounded-full bg-blue-400" /> Custom rules
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <Zap size={10} className="text-indigo-400" /> Creditsafe API rules
            </div>
          </div>
        )}

        {/* Unconfigured warning */}
        {unconfigured.length > 0 && (
          <div className="mx-5 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2">
            <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-amber-700">
                {unconfigured.length} rule{unconfigured.length > 1 ? 's' : ''} without thresholds
              </p>
              <p className="text-[11px] text-amber-600">
                {unconfigured.map((r) => getRuleName(r.ruleCode, r.name)).join(', ')} — won't fire
                until configured.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="text-[11px] font-semibold text-amber-700 border border-amber-300 rounded-lg px-2 py-1 hover:bg-amber-100 flex-shrink-0">
              Fix →
            </button>
          </div>
        )}

        {/* Rule list */}
        <div className="px-5 py-4">
          {rules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-100 rounded-2xl text-center">
              <Bell size={22} className="text-gray-300 mb-3" />
              <p className="text-sm font-bold text-gray-500 mb-1">No rules set up yet</p>
              <p className="text-xs text-gray-400 max-w-xs mb-4 leading-relaxed">
                Add rules to receive alerts on credit changes, insolvency events, and company status
                updates.
              </p>
              <DillButton
                onClick={() => setModalOpen(true)}
                label="Add Monitoring Rules"
                icon={<Plus size={13} />}
              />
            </div>
          ) : (
            activeCategories.map((catKey) => (
              <CategorySection
                key={catKey}
                catKey={catKey}
                rules={grouped[catKey]!}
                onToggle={handleToggle}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {rules.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50 flex items-center justify-between">
            <div className="flex gap-1.5 flex-wrap">
              {activeCategories.map((c) => {
                const cat = CATEGORIES[c];
                const colors = COLOR_MAP[cat.color as ColorType];
                return (
                  <span
                    key={c}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${colors.badge}`}>
                    {cat.icon} {grouped[c]!.length}
                  </span>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="text-[11px] text-gray-400 hover:text-blue-600 font-medium transition-colors">
              + Add custom rule
            </button>
          </div>
        )}
      </div>

      <AddPortfolioRulesModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        existingRules={modalExistingRules}
        onSave={handleModalSave}
      />
    </>
  );
}

// ─────────────────────────────────────────────
// PORTFOLIO EDIT FORM
// ─────────────────────────────────────────────

function PortfolioEditForm({
  portfolio,
  onSave,
  onCancel,
}: {
  portfolio: CreditSafePortfolio;
  onSave: (updated: PortfolioEditState) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<PortfolioEditState>({
    name: portfolio.name,
    frequency: portfolio.frequency as FrequencyOption,
    emailSubject: portfolio.emailSubject,
    emailLanguage: portfolio.emailLanguage,
    isDefault: portfolio.isDefault,
    emails: portfolio.emails.map((e) => ({ ...e })),
  });
  const [newR, setNewR] = useState<EmailRecipient>({
    firstName: '',
    lastName: '',
    emailAddress: '',
  });
  const [recipientError, setRecipientError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = (k: keyof PortfolioEditState, v: any) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[k];
      return n;
    });
  };

  const addRecipient = () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newR.firstName.trim()) {
      setRecipientError('First name required');
      return;
    }
    if (!newR.lastName.trim()) {
      setRecipientError('Last name required');
      return;
    }
    if (!re.test(newR.emailAddress)) {
      setRecipientError('Valid email required');
      return;
    }
    if (form.emails.some((e) => e.emailAddress.toLowerCase() === newR.emailAddress.toLowerCase())) {
      setRecipientError('Already added');
      return;
    }
    setField('emails', [
      ...form.emails,
      { ...newR, emailAddress: newR.emailAddress.toLowerCase() },
    ]);
    setNewR({ firstName: '', lastName: '', emailAddress: '' });
    setRecipientError('');
  };

  const handleSave = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name required';
    setErrors(errs);
    if (!Object.keys(errs).length) onSave(form);
  };

  return (
    <div className="bg-white rounded-2xl border border-blue-200 ring-1 ring-blue-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/60 to-transparent">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
            <Edit2 size={13} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">Edit Portfolio</p>
            <p className="text-xs text-gray-400">
              Updating <span className="font-semibold text-gray-600">{portfolio.name}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-white transition-colors">
          <X size={15} />
        </button>
      </div>

      <div className="px-5 py-5 space-y-6 max-h-[60vh] overflow-y-auto">
        <section>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-4">
            Basic Settings
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Portfolio Name *
              </label>
              <DillInput
                value={form.name}
                onChange={(v) => setField('name', v)}
                placeholder="e.g. Key Suppliers"
                error={errors.name}
              />
              {errors.name && <p className="text-[11px] text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Alert Frequency
              </label>
              <div className="grid grid-cols-4 gap-2">
                {FREQUENCY_OPTIONS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setField('frequency', f)}
                    className={`py-2 rounded-lg text-xs font-semibold border transition-all ${
                      form.frequency === f
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-200 hover:bg-blue-50'
                    }`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Email Language
                </label>
                <DillSelect
                  value={form.emailLanguage}
                  onChange={(v) => setField('emailLanguage', v)}
                  options={LANGUAGE_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Set as Default
                </label>
                <div className="flex items-center gap-2.5 h-[38px]">
                  <DillToggle value={form.isDefault} onChange={(v) => setField('isDefault', v)} />
                  <span className="text-xs text-gray-500">
                    {form.isDefault ? 'Default portfolio' : 'Not default'}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Email Subject
              </label>
              <DillInput
                value={form.emailSubject}
                onChange={(v) => setField('emailSubject', v)}
                placeholder="e.g. Credit Risk Alert"
              />
            </div>
          </div>
        </section>

        <section className="border-t border-gray-100 pt-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
              Recipients
            </p>
            <span className="text-[11px] text-gray-400">{form.emails.length} added</span>
          </div>
          {form.emails.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.emails.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-blue-600">
                      {e.firstName[0]}
                      {e.lastName[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800">
                      {e.firstName} {e.lastName}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">{e.emailAddress}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setField(
                        'emails',
                        form.emails.filter((_, j) => j !== i)
                      )
                    }
                    className="text-gray-300 hover:text-red-400 p-1 rounded hover:bg-red-50">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4">
            <p className="text-[11px] font-semibold text-gray-500 mb-3 flex items-center gap-1.5">
              <UserPlus size={11} /> Add recipient
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <DillInput
                value={newR.firstName}
                onChange={(v) => {
                  setNewR((p) => ({ ...p, firstName: v }));
                  setRecipientError('');
                }}
                placeholder="First name"
              />
              <DillInput
                value={newR.lastName}
                onChange={(v) => {
                  setNewR((p) => ({ ...p, lastName: v }));
                  setRecipientError('');
                }}
                placeholder="Last name"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <DillInput
                  value={newR.emailAddress}
                  type="email"
                  onChange={(v) => {
                    setNewR((p) => ({ ...p, emailAddress: v }));
                    setRecipientError('');
                  }}
                  placeholder="email@company.com"
                />
              </div>
              <DillButton
                onClick={addRecipient}
                label="Add"
                icon={<Plus size={12} />}
                variant="secondary"
              />
            </div>
            {recipientError && <p className="text-[11px] text-red-500 mt-1.5">{recipientError}</p>}
          </div>
        </section>
      </div>

      <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/40">
        <DillButton onClick={onCancel} label="Discard" icon={<X size={12} />} variant="secondary" />
        <DillButton onClick={handleSave} label="Save Portfolio" icon={<Save size={12} />} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PORTFOLIO INFO CARD (read mode)
// ─────────────────────────────────────────────

function PortfolioInfoCard({
  portfolio,
  onEdit,
}: {
  portfolio: CreditSafePortfolio;
  onEdit: () => void;
}) {
  const freqColor =
    FREQUENCY_COLORS[portfolio.frequency] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  const langLabel =
    LANGUAGE_OPTIONS.find((l) => l.value === portfolio.emailLanguage)?.label ??
    portfolio.emailLanguage;
  const activeCount = (portfolio.rules ?? []).filter((r) => r.isActive === 1).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-extrabold text-gray-900">{portfolio.name}</h2>
              {portfolio.isDefault && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-2 py-0.5">
                  <Star size={8} fill="currentColor" /> Default
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400">Portfolio #{portfolio.portfolioId}</p>
          </div>
        </div>
        <DillButton onClick={onEdit} label="Edit" icon={<Edit2 size={12} />} variant="secondary" />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        {[
          {
            icon: <Clock size={12} className="text-gray-400" />,
            label: 'Frequency',
            val: (
              <span
                className={`inline-flex items-center text-[11px] font-semibold border rounded-full px-2 py-0.5 ${freqColor}`}>
                {portfolio.frequency}
              </span>
            ),
          },
          {
            icon: <Hash size={12} className="text-gray-400" />,
            label: 'Portfolio ID',
            val: <span className="font-mono text-xs text-gray-700">{portfolio.portfolioId}</span>,
          },
          {
            icon: <Globe size={12} className="text-gray-400" />,
            label: 'Language',
            val: <span className="text-xs text-gray-700">{langLabel}</span>,
          },
          {
            icon: <Bell size={12} className="text-gray-400" />,
            label: 'Active Rules',
            val: <span className="text-xs font-bold text-gray-900">{activeCount}</span>,
          },
        ].map(({ icon, label, val }) => (
          <div key={label} className="flex items-start gap-2">
            <span className="mt-0.5 flex-shrink-0">{icon}</span>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                {label}
              </p>
              <div className="mt-0.5">{val}</div>
            </div>
          </div>
        ))}
      </div>

      {portfolio.emailSubject && (
        <div className="flex items-start gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
          <Mail size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">
              Email Subject
            </p>
            <p className="text-xs text-gray-700">{portfolio.emailSubject}</p>
          </div>
        </div>
      )}

      <div className="border-t border-gray-50 pt-4">
        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
          <Users size={10} /> Recipients
        </p>
        {portfolio.emails.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {portfolio.emails.map((e, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-full pl-1 pr-2.5 py-1">
                <span className="w-4 h-4 rounded-full bg-blue-500 inline-flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0">
                  {e.firstName[0]}
                  {e.lastName[0]}
                </span>
                {e.firstName} {e.lastName}
              </span>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-100 bg-amber-50">
            <AlertTriangle size={13} className="text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-amber-700">No recipients configured</p>
              <p className="text-[11px] text-amber-600">
                Alerts won't be emailed until recipients are added.
              </p>
            </div>
            <button
              type="button"
              onClick={onEdit}
              className="text-[11px] font-semibold text-amber-700 underline underline-offset-2">
              Add →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// CUSTOMER CARD (read mode)
// ─────────────────────────────────────────────

function CustomerCard({
  portfolio,
  onEdit,
}: {
  portfolio: CreditSafePortfolio;
  onEdit: (data: { creditSafeId: string; name: string }[]) => void;
}) {
  const { creditSafeCompanies } = useAppSelector((state) => state.creditSafe);

  const [openCreditSafeCompanyModal, setOpenCreditSafeCompanyModal] = useState<boolean>(false);

  // ✅ LOCAL STATE (important)
  const [selectedCompanies, setSelectedCompanies] = useState<
    { name: string; creditSafeId: string }[]
  >(() =>
    (portfolio?.companies ?? []).map((c) => ({
      name: c.name,
      creditSafeId: c.creditSafeId,
    }))
  );

  // Existing companies
  const companies = selectedCompanies;

  // Options to select (exclude already selected)
  const companiesToSelect = useMemo(
    () =>
      (creditSafeCompanies ?? [])
        .filter((c) => !companies.find((cc) => cc.creditSafeId === c.creditSafeId))
        .map((c) => ({
          label: c.name,
          value: c.creditSafeId,
        })),
    [creditSafeCompanies, companies]
  );

  const handleAddCompany = (id: string) => {
    const company = creditSafeCompanies.find((c) => c.creditSafeId === id);
    if (!company) return;
    const updatedList = [
      ...selectedCompanies,
      ...[{ name: company.name, creditSafeId: company.creditSafeId }],
    ];

    setSelectedCompanies((prev) => [
      ...prev,
      { name: company.name, creditSafeId: company.creditSafeId },
    ]);

    onEdit(updatedList);

    setOpenCreditSafeCompanyModal(false);
  };

  // ✅ REMOVE COMPANY
  const handleRemoveCompany = (id: string) => {
    setSelectedCompanies((prev) => prev.filter((c) => c.creditSafeId !== id));

    onEdit(selectedCompanies.filter((c) => c.creditSafeId !== id));
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      {/* Modal */}
      <SelectCreditSafeCompanyModal
        open={openCreditSafeCompanyModal}
        handleClose={() => setOpenCreditSafeCompanyModal(false)}
        onSubmit={handleAddCompany}
        selectList={companiesToSelect}
      />

      {/* Header */}
      <div className="flex w-full items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <BuildingOffice size={15} className="text-blue-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Companies</h3>
            <p className="text-xs text-gray-400">
              {companies.length === 0
                ? 'No companies added'
                : `${companies.length} compan${companies.length === 1 ? 'y' : 'ies'}`}
            </p>
          </div>
        </div>

        <DillButton
          onClick={() => setOpenCreditSafeCompanyModal(true)}
          label="Add Company"
          icon={<Plus size={13} />}
        />
      </div>

      {/* EMPTY STATE */}
      {companies.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-xl">
          <p className="text-sm text-gray-400">No companies added yet</p>
          <DillButton
            onClick={() => setOpenCreditSafeCompanyModal(true)}
            label="Add Company"
            icon={<Plus size={13} />}
          />
        </div>
      ) : (
        /* ✅ GRID */
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {companies.map((c) => (
            <div
              key={c.creditSafeId}
              className="flex items-center justify-between gap-2 border border-gray-100 rounded-xl px-3 py-2 bg-gray-50 hover:bg-white transition">
              {/* Company Info */}
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">{c.name}</p>
                <p className="text-[10px] text-gray-400 truncate">ID: {c.creditSafeId}</p>
              </div>

              {/* Remove */}
              <button
                onClick={() => handleRemoveCompany(c.creditSafeId)}
                className="text-gray-300 hover:text-red-500 p-1 rounded hover:bg-red-50 transition">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ROOT EXPORT
// ─────────────────────────────────────────────

export default function PortfolioDetail({
  portfolio,
  rules,
  onBack,
  onSavePortfolio,
  onRulesChange,
  onEditCompany,
}: PortfolioDetailProps) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="bg-gray-50/70">
      <div className="w-full">
        <div className="w-full flex item-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-600 mb-4 group transition-colors">
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
            <span>Portfolios</span>
            <span className="text-gray-300 mx-1">/</span>
            <span className="text-gray-800 font-semibold">{portfolio.name}</span>
          </button>
          {/* <AppButton
            onClick={() => {}}
            text="Save"
            buttonStyles={{
              height: '30px',
              width: '100px',
            }}
          /> */}
        </div>

        <div className="h-full overflow-y-auto">
          <div className="flex flex-col gap-4">
            {editing ? (
              <PortfolioEditForm
                portfolio={portfolio}
                onSave={(updated) => {
                  onSavePortfolio(updated);
                  setEditing(false);
                }}
                onCancel={() => setEditing(false)}
              />
            ) : (
              <PortfolioInfoCard portfolio={portfolio} onEdit={() => setEditing(true)} />
            )}

            <CustomerCard portfolio={portfolio} onEdit={onEditCompany} />

            {!editing && (
              <RulesPanel portfolio={portfolio} rules={rules} onRulesChange={onRulesChange} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
